// Facility HR client — 1:1 port of legacy
// `src/components/account/facility-hr-client.tsx` adapted for the TanStack
// Router SPA shell. Differences vs legacy:
//   - i18n via Lingui `useLingui`/`<Trans>` (legacy used next-intl namespace
//     `facility.hr`); dynamic type/status labels go through switch helpers.
//   - Data via TanStack Query + `apiFetch` (legacy used raw fetch plus a
//     `useState(() => { load(); })` mount hack).
//   - Modals via `<ResponsiveSheet>` (legacy used shadcn Dialog) — mandatory
//     per CLAUDE.md.
//   - Mutations surface the standard saving→saved/error toast (`showToast`,
//     per feedback_save_toast_pattern) — legacy saved silently.
//   - Attendance table gains the mobile expandable-row accordion per
//     CLAUDE.md (legacy squeezed the times under the name cell).
//   - Status pills use brand-token Badge variants (legacy used raw palette
//     classes like bg-green-100).
//   - Tab URL state (`?tab=`) is owned by the route file (validateSearch) and
//     passed down as `activeTab`/`onTabChange`.

import { Fragment, useId, useRef, useState, type ComponentProps, type ReactNode } from "react";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { m } from "framer-motion";
import {
  AlertCircle,
  Calendar,
  Check,
  ChevronDown,
  Clock,
  FileText,
  LogIn,
  LogOut,
  Plus,
  Users,
  X,
} from "lucide-react";

import { roleLabel } from "@/components/account/facility/facility-staff-client";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FilterChip } from "@/components/ui/filter-chip";
import { Input } from "@/components/ui/input";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";
import { showToast } from "@/lib/ui/toast";

// ─── Types ────────────────────────────────────────────────────────────────

export interface HrStaffMember {
  id: string;
  userId: string;
  role: string;
  user: {
    id: string;
    email: string | null;
    phone?: string | null;
    profile?: { firstName: string | null; lastName: string | null } | null;
  };
}

/** Person's name for HR lists (the /hr/staff payload carries the profile);
 *  falls back to a real email, then the phone — never a synthetic address. */
function staffDisplayName(member: HrStaffMember | undefined, fallbackEmail?: string | null) {
  const profile = member?.user.profile;
  const name = profile ? `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim() : "";
  if (name) return name;
  const email = member?.user.email ?? fallbackEmail ?? null;
  if (email && !/@(phone|oauth)\.pontimed\.app$/.test(email)) return email;
  return member?.user.phone || email || "—";
}

interface StaffSchedule {
  id: string;
  staffId: string;
  dayOfWeek: number;
  shiftStart: string;
  shiftEnd: string;
  breakStart?: string | null;
  breakEnd?: string | null;
  isActive: boolean;
}

interface AttendanceRecord {
  id: string;
  staffId: string;
  date: string;
  checkIn?: string | null;
  checkOut?: string | null;
  breakMinutes?: number | null;
  workedMinutes?: number | null;
  status: string;
  notes?: string | null;
  staff: { user: { id: string; email: string | null } };
}

interface LeaveRequest {
  id: string;
  staffId: string;
  startDate: string;
  endDate: string;
  type: string;
  reason?: string | null;
  status: string;
  approvedAt?: string | null;
  declineReason?: string | null;
  staff: { user: { id: string; email: string | null } };
  approvedBy?: { id: string; email: string | null } | null;
}

export const HR_TAB_IDS = ["attendance", "schedule", "leave"] as const;
export type FacilityHrTab = (typeof HR_TAB_IDS)[number];

interface FacilityHrClientProps {
  facilityId: string;
  staffMembers: HrStaffMember[];
  currentUserRole: string;
  currentUserId: string;
  activeTab: FacilityHrTab;
  onTabChange: (tab: FacilityHrTab) => void;
}

// ─── Constants + helpers ──────────────────────────────────────────────────

const DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 0]; // Mon→Sun display order
const LEAVE_TYPES = ["vacation", "sick", "personal", "maternity", "other"] as const;
const MANAGER_ROLES = ["owner", "director", "facility_manager", "admin"];

type TFn = ReturnType<typeof useLingui>["t"];
type BadgeVariant = ComponentProps<typeof Badge>["variant"];

function formatMinutes(mins: number | null | undefined): string {
  if (!mins) return "—";
  const h = Math.floor(mins / 60);
  const min = mins % 60;
  return min === 0 ? `${h}h` : `${h}h ${min}m`;
}

function formatTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

// UTC day on purpose: the attendance API files every check-in under the UTC
// date (account-facility-staff.ts), so a local key would hide a check-in made
// after local midnight and the next tap would check the person out.
/** Leave period in the UI locale ("11 oct. – 14 oct. 2026"), not raw ISO. */
function formatDateRange(startIso: string, endIso: string, locale: string): string {
  const start = new Date(`${startIso.slice(0, 10)}T00:00:00`);
  const end = new Date(`${endIso.slice(0, 10)}T00:00:00`);
  const fmt = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" });
  try {
    return fmt.formatRange(start, end);
  } catch {
    return `${fmt.format(start)} – ${fmt.format(end)}`;
  }
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function hrQueryKey(facilityId: string, ...rest: (string | number)[]) {
  return ["facility", facilityId, "hr", ...rest] as const;
}

// Dynamic labels — every backend enum value gets an explicit Lingui msgid so
// translators see real strings (task rule: translate dynamic type/status
// labels; never render the raw enum).

function dayLabel(dayOfWeek: number, t: TFn): string {
  switch (dayOfWeek) {
    case 1:
      return t(msg`Mon`);
    case 2:
      return t(msg`Tue`);
    case 3:
      return t(msg`Wed`);
    case 4:
      return t(msg`Thu`);
    case 5:
      return t(msg`Fri`);
    case 6:
      return t(msg`Sat`);
    default:
      return t(msg`Sun`);
  }
}

function attendanceStatusLabel(status: string, t: TFn): string {
  switch (status) {
    case "present":
      return t(msg`Present`);
    case "absent":
      return t(msg`Absent`);
    case "late":
      return t(msg`Late`);
    case "half_day":
      return t(msg`Half day`);
    case "remote":
      return t(msg`Remote`);
    default:
      return status;
  }
}

function attendanceStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case "present":
      return "success";
    case "absent":
      return "destructive";
    case "late":
      return "warning";
    case "half_day":
      return "info";
    case "remote":
      return "accent";
    default:
      return "neutral";
  }
}

function leaveTypeLabel(type: string, t: TFn): string {
  switch (type) {
    case "vacation":
      return t(msg`Vacation`);
    case "sick":
      return t(msg`Sick leave`);
    case "personal":
      return t(msg`Personal`);
    case "maternity":
      return t(msg`Maternity`);
    case "other":
      return t(msg`Other`);
    default:
      return type;
  }
}

function leaveStatusLabel(status: string, t: TFn): string {
  switch (status) {
    case "pending":
      return t(msg`Pending`);
    case "approved":
      return t(msg`Approved`);
    case "declined":
      return t(msg`Declined`);
    case "cancelled":
      return t(msg`Cancelled`);
    default:
      return status;
  }
}

function leaveStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case "pending":
      return "warning";
    case "approved":
      return "success";
    case "declined":
      return "destructive";
    default:
      return "neutral";
  }
}

// Small form-field wrapper (apps/app has no ui/label component).
/** `htmlFor` must match the `id` of the control inside. */
function Field({
  label,
  htmlFor,
  children,
}: {
  label: ReactNode;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-foreground mb-1.5 block text-sm font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}

// ─── Tab: Attendance ──────────────────────────────────────────────────────

function AttendanceTab({
  facilityId,
  staffMembers,
  isManager,
  currentUserId,
}: {
  facilityId: string;
  staffMembers: HrStaffMember[];
  isManager: boolean;
  currentUserId: string;
}) {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();
  const [date, setDate] = useState(todayISO());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actingStaffId, setActingStaffId] = useState<string | null>(null);

  const recordsQuery = useQuery({
    queryKey: hrQueryKey(facilityId, "attendance", date),
    queryFn: () =>
      apiFetch.get<{ records: AttendanceRecord[] }>(
        `/api/account/facility/${facilityId}/hr/attendance?startDate=${date}&endDate=${date}`,
      ),
    enabled: date !== "",
  });
  const records = recordsQuery.data?.records ?? [];

  const checkMutation = useMutation({
    mutationFn: (staffId: string) =>
      apiFetch.post<{ record: AttendanceRecord }>(
        `/api/account/facility/${facilityId}/hr/attendance`,
        { staffId },
      ),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: hrQueryKey(facilityId, "attendance") }),
  });

  async function handleCheckAction(staffId: string) {
    setActingStaffId(staffId);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await checkMutation.mutateAsync(staffId);
      toast.update(t`Saved`, "success");
    } catch {
      toast.update(t`Save failed`, "error");
    } finally {
      setActingStaffId(null);
    }
  }

  return (
    <div className="space-y-4">
      {/* Date picker */}
      <div className="flex items-center gap-3">
        <label htmlFor="hr-attendance-date" className="text-foreground text-sm font-medium">
          <Trans>Date</Trans>
        </label>
        <Input
          id="hr-attendance-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-44"
        />
        <Button variant="outline" size="touch" onClick={() => void recordsQuery.refetch()}>
          <Trans>Load</Trans>
        </Button>
      </div>

      {recordsQuery.isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-muted h-14 animate-pulse rounded-lg" />
          ))}
        </div>
      )}

      {!recordsQuery.isLoading && (
        <div className="border-border overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-border bg-muted/50 border-b">
                <th className="text-muted-foreground px-4 py-3 text-left font-medium">
                  <Trans>Staff member</Trans>
                </th>
                <th className="text-muted-foreground hidden px-4 py-3 text-left font-medium md:table-cell">
                  <Trans>Check in</Trans>
                </th>
                <th className="text-muted-foreground hidden px-4 py-3 text-left font-medium md:table-cell">
                  <Trans>Check out</Trans>
                </th>
                <th className="text-muted-foreground hidden px-4 py-3 text-left font-medium lg:table-cell">
                  <Trans>Worked hours</Trans>
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left font-medium">
                  <Trans>Status</Trans>
                </th>
                <th className="text-muted-foreground px-4 py-3 text-right font-medium">
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {staffMembers.map((member) => {
                const record = records.find((r) => r.staffId === member.id);
                const isCurrentUser = member.userId === currentUserId;
                const canAct = isManager || isCurrentUser;
                const isCheckedIn = !!record?.checkIn && !record?.checkOut;
                const isCheckedOut = !!record?.checkIn && !!record?.checkOut;
                const isActing = actingStaffId === member.id;
                const isExpanded = expandedId === member.id;

                return (
                  <Fragment key={member.id}>
                    <tr
                      className="border-border hover:bg-muted/30 border-b last:border-0"
                      onClick={() => setExpandedId(isExpanded ? null : member.id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium">{staffDisplayName(member)}</span>
                          {/* Keyboard/screen-reader toggle for the mobile
                              accordion (the row itself is also tappable). */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedId(isExpanded ? null : member.id);
                            }}
                            aria-expanded={isExpanded}
                            aria-label={t`Show details`}
                            className="text-muted-foreground hover:bg-muted -my-2 flex size-11 shrink-0 items-center justify-center rounded-lg md:hidden"
                          >
                            <ChevronDown
                              className={cn(
                                "size-4 transition-transform",
                                isExpanded && "rotate-180",
                              )}
                              aria-hidden
                            />
                          </button>
                        </div>
                      </td>
                      <td className="hidden px-4 py-3 md:table-cell">
                        {formatTime(record?.checkIn, i18n.locale)}
                      </td>
                      <td className="hidden px-4 py-3 md:table-cell">
                        {formatTime(record?.checkOut, i18n.locale)}
                      </td>
                      <td className="hidden px-4 py-3 lg:table-cell">
                        {formatMinutes(record?.workedMinutes)}
                      </td>
                      <td className="px-4 py-3">
                        {record ? (
                          <Badge variant={attendanceStatusVariant(record.status)}>
                            {attendanceStatusLabel(record.status, t)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canAct && !isCheckedOut && (
                          <Button
                            size="touch"
                            variant={isCheckedIn ? "outline" : "default"}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleCheckAction(member.id);
                            }}
                            disabled={isActing}
                          >
                            {isCheckedIn ? (
                              <>
                                <LogOut className="size-3.5" aria-hidden />
                                <Trans>Check out</Trans>
                              </>
                            ) : (
                              <>
                                <LogIn className="size-3.5" aria-hidden />
                                <Trans>Check in</Trans>
                              </>
                            )}
                          </Button>
                        )}
                        {isCheckedOut && (
                          <span className="text-muted-foreground text-xs">
                            <Trans>Done</Trans>
                          </span>
                        )}
                      </td>
                    </tr>

                    {/* Mobile accordion — full data for columns hidden < md */}
                    {isExpanded && (
                      <tr className="border-border bg-muted/20 border-b last:border-0 md:hidden">
                        <td colSpan={6} className="px-4 py-3">
                          <dl className="space-y-1.5 text-sm">
                            <div className="flex items-center justify-between gap-3">
                              <dt className="text-muted-foreground">
                                <Trans>Check in</Trans>
                              </dt>
                              <dd className="text-foreground">
                                {formatTime(record?.checkIn, i18n.locale)}
                              </dd>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <dt className="text-muted-foreground">
                                <Trans>Check out</Trans>
                              </dt>
                              <dd className="text-foreground">
                                {formatTime(record?.checkOut, i18n.locale)}
                              </dd>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <dt className="text-muted-foreground">
                                <Trans>Worked hours</Trans>
                              </dt>
                              <dd className="text-foreground">
                                {formatMinutes(record?.workedMinutes)}
                              </dd>
                            </div>
                          </dl>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}

              {staffMembers.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted-foreground py-12 text-center">
                    <Trans>No staff members found</Trans>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Schedule ────────────────────────────────────────────────────────

function ScheduleTab({
  facilityId,
  staffMembers,
  isManager,
}: {
  facilityId: string;
  staffMembers: HrStaffMember[];
  isManager: boolean;
}) {
  const { t } = useLingui();
  const uid = useId();
  const queryClient = useQueryClient();
  const [editModal, setEditModal] = useState<{
    staffId: string;
    dayOfWeek: number;
    existing?: StaffSchedule;
  } | null>(null);
  const [formState, setFormState] = useState({
    shiftStart: "08:00",
    shiftEnd: "16:00",
    breakStart: "",
    breakEnd: "",
  });
  const [saving, setSaving] = useState(false);

  const schedulesQuery = useQuery({
    queryKey: hrQueryKey(facilityId, "schedule"),
    queryFn: () =>
      apiFetch.get<{ schedules: StaffSchedule[] }>(
        `/api/account/facility/${facilityId}/hr/schedule`,
      ),
  });
  const schedules = schedulesQuery.data?.schedules ?? [];

  const invalidateSchedules = () =>
    queryClient.invalidateQueries({ queryKey: hrQueryKey(facilityId, "schedule") });

  const upsertMutation = useMutation({
    mutationFn: (input: {
      staffId: string;
      dayOfWeek: number;
      shiftStart: string;
      shiftEnd: string;
      breakStart: string | null;
      breakEnd: string | null;
    }) =>
      apiFetch.post<{ schedule: StaffSchedule }>(
        `/api/account/facility/${facilityId}/hr/schedule`,
        input,
      ),
    onSettled: invalidateSchedules,
  });

  const deleteMutation = useMutation({
    mutationFn: async (input: { staffId: string; dayOfWeek: number }) => {
      // apiFetch.delete() takes no body; this endpoint requires one.
      const res = await apiFetch.raw(`/api/account/facility/${facilityId}/hr/schedule`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new ApiError(res.status, null);
    },
    onSettled: invalidateSchedules,
  });

  function openEdit(staffId: string, dayOfWeek: number) {
    if (!isManager) return;
    const existing = schedules.find((s) => s.staffId === staffId && s.dayOfWeek === dayOfWeek);
    setFormState({
      shiftStart: existing?.shiftStart || "08:00",
      shiftEnd: existing?.shiftEnd || "16:00",
      breakStart: existing?.breakStart || "",
      breakEnd: existing?.breakEnd || "",
    });
    setEditModal({ staffId, dayOfWeek, existing });
  }

  async function saveShift() {
    if (!editModal) return;
    setSaving(true);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await upsertMutation.mutateAsync({
        staffId: editModal.staffId,
        dayOfWeek: editModal.dayOfWeek,
        shiftStart: formState.shiftStart,
        shiftEnd: formState.shiftEnd,
        breakStart: formState.breakStart || null,
        breakEnd: formState.breakEnd || null,
      });
      toast.update(t`Saved`, "success");
      setEditModal(null);
    } catch {
      toast.update(t`Save failed`, "error");
    } finally {
      setSaving(false);
    }
  }

  async function deleteShift() {
    if (!editModal?.existing) return;
    setSaving(true);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await deleteMutation.mutateAsync({
        staffId: editModal.staffId,
        dayOfWeek: editModal.dayOfWeek,
      });
      toast.update(t`Saved`, "success");
      setEditModal(null);
    } catch {
      toast.update(t`Save failed`, "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {schedulesQuery.isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-muted h-12 animate-pulse rounded-lg" />
          ))}
        </div>
      )}

      {!schedulesQuery.isLoading && staffMembers.length === 0 && (
        <div className="text-muted-foreground py-12 text-center">
          <Users className="mx-auto mb-2 size-8 opacity-40" aria-hidden />
          <p>
            <Trans>No staff members found</Trans>
          </p>
        </div>
      )}

      {!schedulesQuery.isLoading && staffMembers.length > 0 && (
        <div className="border-border overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[700px] text-sm">
            <thead>
              <tr className="border-border bg-muted/50 border-b">
                {/* Sticky name column: scrolling to Thursday keeps whose row
                    it is in view. */}
                <th className="text-muted-foreground bg-muted sticky left-0 z-10 px-4 py-3 text-left font-medium">
                  <Trans>Staff member</Trans>
                </th>
                {DAYS_OF_WEEK.map((d) => (
                  <th key={d} className="text-muted-foreground px-2 py-3 text-center font-medium">
                    {dayLabel(d, t)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staffMembers.map((member) => (
                <tr
                  key={member.id}
                  className="border-border hover:bg-muted/30 border-b last:border-0"
                >
                  <td className="bg-card sticky left-0 z-10 px-4 py-3">
                    <div className="font-medium">{staffDisplayName(member)}</div>
                    <div className="text-muted-foreground text-xs">{roleLabel(member.role)}</div>
                  </td>
                  {DAYS_OF_WEEK.map((d) => {
                    const shift = schedules.find(
                      (s) => s.staffId === member.id && s.dayOfWeek === d,
                    );
                    return (
                      <td key={d} className="px-2 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => openEdit(member.id, d)}
                          disabled={!isManager}
                          className={cn(
                            "min-h-[44px] w-full rounded-md px-1 py-1 text-xs transition-colors",
                            shift
                              ? "bg-primary-soft text-primary hover:bg-primary/15"
                              : "text-muted-foreground hover:bg-muted",
                            !isManager && "cursor-default",
                          )}
                        >
                          {shift ? (
                            <span>
                              {shift.shiftStart}
                              <br />
                              {shift.shiftEnd}
                            </span>
                          ) : (
                            <span className="opacity-40">—</span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit shift sheet */}
      <ResponsiveSheet
        open={editModal !== null}
        onClose={() => setEditModal(null)}
        title={t`Edit shift`}
        width="sm"
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            {editModal?.existing && (
              <Button
                size="touch"
                variant="destructive"
                onClick={() => void deleteShift()}
                disabled={saving}
                className="min-h-[44px]"
              >
                <Trans>Day off</Trans>
              </Button>
            )}
            <Button
              size="touch"
              variant="outline"
              onClick={() => setEditModal(null)}
              className="min-h-[44px]"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              size="touch"
              onClick={() => void saveShift()}
              disabled={saving}
              className="min-h-[44px]"
            >
              {saving ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label={<Trans>Shift start</Trans>} htmlFor={`${uid}-shift-start`}>
              <Input
                id={`${uid}-shift-start`}
                type="time"
                value={formState.shiftStart}
                onChange={(e) => setFormState((p) => ({ ...p, shiftStart: e.target.value }))}
              />
            </Field>
            <Field label={<Trans>Shift end</Trans>} htmlFor={`${uid}-shift-end`}>
              <Input
                id={`${uid}-shift-end`}
                type="time"
                value={formState.shiftEnd}
                onChange={(e) => setFormState((p) => ({ ...p, shiftEnd: e.target.value }))}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={<Trans>Break start</Trans>} htmlFor={`${uid}-break-start`}>
              <Input
                id={`${uid}-break-start`}
                type="time"
                value={formState.breakStart}
                onChange={(e) => setFormState((p) => ({ ...p, breakStart: e.target.value }))}
                placeholder="—"
              />
            </Field>
            <Field label={<Trans>Break end</Trans>} htmlFor={`${uid}-break-end`}>
              <Input
                id={`${uid}-break-end`}
                type="time"
                value={formState.breakEnd}
                onChange={(e) => setFormState((p) => ({ ...p, breakEnd: e.target.value }))}
                placeholder="—"
              />
            </Field>
          </div>
        </div>
      </ResponsiveSheet>
    </div>
  );
}

// ─── Tab: Leave requests ──────────────────────────────────────────────────

function LeaveTab({
  facilityId,
  staffMembers,
  isManager,
  currentUserId,
}: {
  facilityId: string;
  staffMembers: HrStaffMember[];
  isManager: boolean;
  currentUserId: string;
}) {
  const { t, i18n } = useLingui();
  const uid = useId();
  const queryClient = useQueryClient();
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    staffId: "",
    startDate: todayISO(),
    endDate: todayISO(),
    type: "vacation" as string,
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [declineModal, setDeclineModal] = useState<{ id: string } | null>(null);
  const [declineReason, setDeclineReason] = useState("");

  const requestsQuery = useQuery({
    queryKey: hrQueryKey(facilityId, "leave", filterStatus),
    queryFn: () =>
      apiFetch.get<{ requests: LeaveRequest[] }>(
        `/api/account/facility/${facilityId}/hr/leave${
          filterStatus !== "all" ? `?status=${filterStatus}` : ""
        }`,
      ),
  });
  const requests = requestsQuery.data?.requests ?? [];

  const invalidateLeave = () =>
    queryClient.invalidateQueries({ queryKey: hrQueryKey(facilityId, "leave") });

  const submitMutation = useMutation({
    mutationFn: (input: typeof requestForm) =>
      apiFetch.post<{ request: LeaveRequest }>(
        `/api/account/facility/${facilityId}/hr/leave`,
        input,
      ),
    onSettled: invalidateLeave,
  });

  const actionMutation = useMutation({
    mutationFn: (input: {
      id: string;
      action: "approve" | "decline" | "cancel";
      declineReason?: string;
    }) =>
      apiFetch.patch<{ request: LeaveRequest }>(
        `/api/account/facility/${facilityId}/hr/leave`,
        input,
      ),
    onSettled: invalidateLeave,
  });

  async function submitRequest() {
    setSubmitting(true);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await submitMutation.mutateAsync(requestForm);
      toast.update(t`Saved`, "success");
      setShowRequestModal(false);
    } catch {
      toast.update(t`Save failed`, "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAction(id: string, action: "approve" | "decline" | "cancel", dr?: string) {
    setActingId(id);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await actionMutation.mutateAsync({ id, action, declineReason: dr });
      toast.update(t`Saved`, "success");
    } catch {
      toast.update(t`Save failed`, "error");
    } finally {
      setActingId(null);
    }
  }

  // Find current user's staff record — preset for the request form.
  const myStaffId = staffMembers.find((mem) => mem.userId === currentUserId)?.id;

  const statusFilters: { id: string; label: string }[] = [
    { id: "all", label: t`All` },
    { id: "pending", label: t`Pending` },
    { id: "approved", label: t`Approved` },
    { id: "declined", label: t`Declined` },
  ];

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((s) => (
            <FilterChip
              key={s.id}
              active={filterStatus === s.id}
              onClick={() => setFilterStatus(s.id)}
            >
              {s.label}
            </FilterChip>
          ))}
        </div>
        <Button
          size="touch"
          onClick={() => {
            setRequestForm((p) => ({ ...p, staffId: myStaffId || "" }));
            setShowRequestModal(true);
          }}
        >
          <Plus className="size-4" aria-hidden />
          <Trans>Request leave</Trans>
        </Button>
      </div>

      {requestsQuery.isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-muted h-16 animate-pulse rounded-lg" />
          ))}
        </div>
      )}

      {!requestsQuery.isLoading && requests.length === 0 && (
        <div className="text-muted-foreground py-12 text-center">
          <FileText className="mx-auto mb-2 size-8 opacity-40" aria-hidden />
          <p>
            <Trans>No leave requests</Trans>
          </p>
        </div>
      )}

      {!requestsQuery.isLoading && requests.length > 0 && (
        <div className="space-y-2">
          {requests.map((req) => {
            const isOwn = req.staff.user.id === currentUserId;
            const isPending = req.status === "pending";
            const isActingThis = actingId === req.id;
            const member = staffMembers.find((mem) => mem.id === req.staffId);
            const dateRange = formatDateRange(req.startDate, req.endDate, i18n.locale);

            return (
              <m.div
                key={req.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="border-border bg-card flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">
                      {staffDisplayName(member, req.staff.user.email)}
                    </span>
                    <Badge variant={leaveStatusVariant(req.status)}>
                      {leaveStatusLabel(req.status, t)}
                    </Badge>
                    <Badge variant="neutral">{leaveTypeLabel(req.type, t)}</Badge>
                  </div>
                  <div className="text-muted-foreground text-sm">{dateRange}</div>
                  {req.reason && <div className="text-muted-foreground text-sm">{req.reason}</div>}
                  {req.declineReason && (
                    <div className="text-destructive flex items-center gap-1 text-sm">
                      <AlertCircle className="size-3.5 shrink-0" aria-hidden />
                      {req.declineReason}
                    </div>
                  )}
                </div>

                {isPending && (
                  <div className="flex flex-wrap gap-2">
                    {isManager && (
                      <>
                        <Button
                          size="touch"
                          onClick={() => void handleAction(req.id, "approve")}
                          disabled={isActingThis}
                          className="bg-success text-success-foreground hover:bg-success/90"
                        >
                          <Check className="size-3.5" aria-hidden />
                          <Trans>Approve</Trans>
                        </Button>
                        <Button
                          size="touch"
                          variant="destructive"
                          onClick={() => {
                            setDeclineModal({ id: req.id });
                            setDeclineReason("");
                          }}
                          disabled={isActingThis}
                        >
                          <X className="size-3.5" aria-hidden />
                          <Trans>Decline</Trans>
                        </Button>
                      </>
                    )}
                    {isOwn && (
                      <Button
                        size="touch"
                        variant="outline"
                        onClick={() => void handleAction(req.id, "cancel")}
                        disabled={isActingThis}
                      >
                        <Trans>Cancel</Trans>
                      </Button>
                    )}
                  </div>
                )}
              </m.div>
            );
          })}
        </div>
      )}

      {/* Request leave sheet */}
      <ResponsiveSheet
        open={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        title={t`Request leave`}
        width="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              size="touch"
              variant="outline"
              onClick={() => setShowRequestModal(false)}
              className="min-h-[44px]"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              size="touch"
              onClick={() => void submitRequest()}
              disabled={submitting || !requestForm.staffId}
              className="min-h-[44px]"
            >
              {submitting ? <Trans>Submitting…</Trans> : <Trans>Submit</Trans>}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {isManager && (
            <Field label={<Trans>Staff member</Trans>} htmlFor={`${uid}-leave-staff`}>
              <Select
                value={requestForm.staffId}
                onValueChange={(v) => setRequestForm((p) => ({ ...p, staffId: String(v ?? "") }))}
              >
                <SelectTrigger id={`${uid}-leave-staff`} className="min-h-[44px] w-full">
                  <SelectValue placeholder={t`Select staff member`}>
                    {(value: string) =>
                      value
                        ? staffDisplayName(staffMembers.find((mem) => mem.id === value))
                        : t`Select staff member`
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {staffMembers.map((mem) => (
                    <SelectItem key={mem.id} value={mem.id}>
                      {staffDisplayName(mem)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label={<Trans>Start date</Trans>} htmlFor={`${uid}-leave-start`}>
              <Input
                id={`${uid}-leave-start`}
                type="date"
                value={requestForm.startDate}
                onChange={(e) => setRequestForm((p) => ({ ...p, startDate: e.target.value }))}
              />
            </Field>
            <Field label={<Trans>End date</Trans>} htmlFor={`${uid}-leave-end`}>
              <Input
                id={`${uid}-leave-end`}
                type="date"
                value={requestForm.endDate}
                onChange={(e) => setRequestForm((p) => ({ ...p, endDate: e.target.value }))}
              />
            </Field>
          </div>
          <Field label={<Trans>Leave type</Trans>} htmlFor={`${uid}-leave-type`}>
            <Select
              value={requestForm.type}
              onValueChange={(v) =>
                setRequestForm((p) => ({ ...p, type: String(v ?? "vacation") }))
              }
            >
              <SelectTrigger id={`${uid}-leave-type`} className="min-h-[44px] w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAVE_TYPES.map((lt) => (
                  <SelectItem key={lt} value={lt}>
                    {leaveTypeLabel(lt, t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={<Trans>Reason</Trans>} htmlFor={`${uid}-leave-reason`}>
            <Input
              id={`${uid}-leave-reason`}
              value={requestForm.reason}
              onChange={(e) => setRequestForm((p) => ({ ...p, reason: e.target.value }))}
              placeholder={t`Optional reason...`}
            />
          </Field>
        </div>
      </ResponsiveSheet>

      {/* Decline reason sheet */}
      <ResponsiveSheet
        open={declineModal !== null}
        onClose={() => setDeclineModal(null)}
        title={t`Decline reason`}
        width="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              size="touch"
              variant="outline"
              onClick={() => setDeclineModal(null)}
              className="min-h-[44px]"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              size="touch"
              variant="destructive"
              className="min-h-[44px]"
              onClick={async () => {
                if (!declineModal) return;
                // Legacy awaited the decline PATCH before closing the modal.
                await handleAction(declineModal.id, "decline", declineReason);
                setDeclineModal(null);
              }}
            >
              <Trans>Decline</Trans>
            </Button>
          </div>
        }
      >
        <Input
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
          placeholder={t`Optional reason for declining...`}
          aria-label={t`Decline reason`}
        />
      </ResponsiveSheet>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────

export function FacilityHrClient({
  facilityId,
  staffMembers,
  currentUserRole,
  currentUserId,
  activeTab,
  onTabChange,
}: FacilityHrClientProps) {
  const { t } = useLingui();
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  const isManager = MANAGER_ROLES.includes(currentUserRole);

  const tabs: TabItem<FacilityHrTab>[] = [
    { id: "attendance", label: t`Attendance`, icon: <Clock className="size-4" /> },
    { id: "schedule", label: t`Schedule`, icon: <Calendar className="size-4" /> },
    { id: "leave", label: t`Leave`, icon: <FileText className="size-4" /> },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        title={<Trans>HR / Staff</Trans>}
        description={<Trans>Shifts, attendance, and leave management</Trans>}
      />

      {/* Tabs */}
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
        className="mt-6"
      />

      {/* Tab content */}
      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={activeTab}
        tabIds={HR_TAB_IDS}
        onTabChange={onTabChange}
        className="mt-4"
        renderPanel={(id) => {
          if (id === "attendance") {
            return (
              <AttendanceTab
                facilityId={facilityId}
                staffMembers={staffMembers}
                isManager={isManager}
                currentUserId={currentUserId}
              />
            );
          }
          if (id === "schedule") {
            return (
              <ScheduleTab
                facilityId={facilityId}
                staffMembers={staffMembers}
                isManager={isManager}
              />
            );
          }
          return (
            <LeaveTab
              facilityId={facilityId}
              staffMembers={staffMembers}
              isManager={isManager}
              currentUserId={currentUserId}
            />
          );
        }}
      />
    </div>
  );
}
